import React, { useEffect, useRef } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, Result } from 'antd';
import type { Location } from 'react-router-dom';
import { selectIsAuthenticated } from '../store/slices/authSlice';
import { canManageAttributes, canManageUsers } from '../types/permissions';
import type { User } from '../types/permissions';

const LOGIN_PATH = '/login';

const isPublicRoute = (pathname: string) => pathname === LOGIN_PATH || pathname.startsWith('/invite');

const buildRedirectTarget = (location: Location) => `${location.pathname}${location.search}${location.hash}`;

export const buildLoginRedirect = (location: Location) =>
  `${LOGIN_PATH}?redirect=${encodeURIComponent(buildRedirectTarget(location))}`;

export const RequireAuth: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const location = useLocation();

  useEffect(() => {
    if (!isAuthenticated) {
      sessionStorage.setItem('postLoginRedirect', buildRedirectTarget(location));
    }
  }, [isAuthenticated, location]);

  if (isAuthenticated) {
    return children;
  }

  return (
    <Navigate
      to={buildLoginRedirect(location)}
      replace
      state={{ redirect: buildRedirectTarget(location) }}
    />
  );
};

/**
 * Админ-гард для `/admin/*`. Бэкенд и так отдаёт 403 на каждый вызов, но без
 * клиентской проверки не-админ, набравший урл руками, получал пустую страницу,
 * забитую отказами, вместо внятного ответа.
 *
 * `permission` обязателен, потому что раздел админки неоднородный: сценарии,
 * сезоны и пользователи гейтятся правом на пользователей, а каталог атрибутов и
 * техкарты — правом на атрибуты. Гард обязан повторять гейт меню, иначе агроном
 * с правами на техкарты получит 403 на своей же странице.
 */
export const RequireAdmin: React.FC<{
  children: React.ReactElement;
  permission: 'users' | 'attributes';
}> = ({ children, permission }) => {
  const user = useSelector((state: { auth: { user: User | null } }) => state.auth.user);
  const navigate = useNavigate();

  const allowed = permission === 'users' ? canManageUsers(user) : canManageAttributes(user);
  if (allowed) {
    return children;
  }

  return (
    <Result
      status="403"
      title="403"
      subTitle="Раздел администрирования доступен только с правом управления пользователями."
      extra={<Button type="primary" onClick={() => navigate('/')}>На главную</Button>}
    />
  );
};

export const AuthRedirectHandler: React.FC = () => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const location = useLocation();
  const navigate = useNavigate();
  const previousAuthRef = useRef(isAuthenticated);

  useEffect(() => {
    const wasAuthenticated = previousAuthRef.current;
    previousAuthRef.current = isAuthenticated;

    if (isAuthenticated || isPublicRoute(location.pathname)) {
      return;
    }

    if (wasAuthenticated) {
      navigate(buildLoginRedirect(location), { replace: true });
    }
  }, [isAuthenticated, location, navigate]);

  return null;
};
